import React from "react";
import CancelOutlined from "@mui/icons-material/CancelOutlined";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import ContentPasteGo from "@mui/icons-material/ContentPasteGo";
import type { DryBulkNominationTask } from "@services/dry-bulk-nomination/types";
import { useTranslation } from "react-i18next";
import DryBulkButton from "./ui/DryBulkButton";

const LANG_BASE = "vessel-nomination.dry-bulk.review";

interface Props {
  testId: string;
  task?: DryBulkNominationTask | null;
  currentUser?: string | null;
  busy?: boolean;
  canDecide?: boolean;
  onClaim: () => void;
  onUnclaim: () => void;
  onApprove?: () => void;
  onReject?: () => void;
}

/** Operations actions on the review task (DBVN-0008, VN-20): Claim, or Unclaim, Reject and Approve for the holder. */
const DryBulkReviewClaimPanel: React.FC<Props> = ({
  testId,
  task,
  currentUser,
  busy,
  canDecide,
  onClaim,
  onUnclaim,
  onApprove,
  onReject,
}) => {
  const { t } = useTranslation();
  if (!task) return null;

  if (task.status === "READY") {
    return (
      <DryBulkButton
        type="button"
        variant="contained"
        color="primary"
        testId={`${testId}-claim`}
        startIcon={<ContentPasteGo />}
        label={t(`${LANG_BASE}.claim`)}
        onClick={onClaim}
        disabled={busy}
      />
    );
  }

  const mine = task.status === "CLAIMED" && !!currentUser && task.claimOwnerName === currentUser;
  if (!mine) return null;

  return (
    <>
      <DryBulkButton
        type="button"
        variant="outlined"
        color="primary"
        testId={`${testId}-unclaim`}
        label={t(`${LANG_BASE}.unclaim`)}
        onClick={onUnclaim}
        disabled={busy}
      />
      {canDecide && (
        <>
          <DryBulkButton
            type="button"
            variant="outlined"
            color="primary"
            testId={`${testId}-reject`}
            startIcon={<CancelOutlined />}
            label={t(`${LANG_BASE}.reject`)}
            onClick={onReject}
            disabled={busy}
          />
          <DryBulkButton
            type="button"
            variant="contained"
            color="primary"
            testId={`${testId}-approve`}
            startIcon={<CheckCircleOutline />}
            label={t(`${LANG_BASE}.approve`)}
            onClick={onApprove}
            disabled={busy}
          />
        </>
      )}
    </>
  );
};

export default DryBulkReviewClaimPanel;
